import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useMemo } from 'react';

import { isType } from '@sorare/core/src/lib/gql';

import type {
  useBenchObjectsTotalScore_composeTeamBenchObjectInterface,
  useBenchObjectsTotalScore_so5AppearanceBonusInterface,
  useBenchObjectsTotalScore_so5RuleInterface,
} from './__generated__/index.graphql.ts';

type Props = {
  displayedTypedRules: useBenchObjectsTotalScore_so5RuleInterface[];
  benchObjects: useBenchObjectsTotalScore_composeTeamBenchObjectInterface[];
  selectedBenchObject:
    | useBenchObjectsTotalScore_composeTeamBenchObjectInterface
    | undefined;
  lineupBonuses?: useBenchObjectsTotalScore_so5AppearanceBonusInterface[];
};

export const computeBenchObjectsTotalScore = ({
  displayedTypedRules,
  benchObjects,
  selectedBenchObject,
  lineupBonuses = [],
}: Props) => {
  const averageScoresBonus = lineupBonuses.find(bonus =>
    isType(bonus, 'So5AverageScoresBonus')
  );

  const averageScoresRule = displayedTypedRules.find(
    rule => rule.__typename === 'So5AverageScoresRule'
  );
  const appearances = displayedTypedRules
    .find(rule => rule.__typename === 'So5AppearancesRule')
    ?.appearances?.filter(({ sub }) => !sub);

  const averageTotalLimit = averageScoresRule?.max ?? null;

  const sortedBenchObjects = [...benchObjects].sort((a, b) => {
    const scoreA = a.lastTenPlayedSo5AverageScore;
    const scoreB = b.lastTenPlayedSo5AverageScore;
    return (scoreB || 0) - (scoreA || 0);
  });

  const totalAverageScore = sortedBenchObjects
    .slice(averageScoresRule?.immuneAppearances || 0)
    .map(
      ({ lastTenPlayedSo5AverageScore }) => lastTenPlayedSo5AverageScore || 0
    )
    .reduce((acc, score) => score + acc, 0);

  const mvpBenchObjectsIds = sortedBenchObjects
    .slice(0, averageScoresRule?.immuneAppearances || 0)
    .map(({ id }) => id);

  const remainingPoints = averageTotalLimit
    ? averageTotalLimit - totalAverageScore
    : undefined;

  const remainingPointsForSlot =
    (remainingPoints ?? 0) +
    (selectedBenchObject?.lastTenPlayedSo5AverageScore ?? 0);

  return {
    total: totalAverageScore,
    lineupBonusCap: averageScoresBonus?.max,
    cap: averageTotalLimit,
    remainingPointsForSlot,
    nbEmptySlots: (appearances || []).length - benchObjects.length,
    mvpBenchIds: mvpBenchObjectsIds,
  };
};

export const useBenchObjectsTotalScore = ({
  displayedTypedRules,
  benchObjects,
  selectedBenchObject,
  lineupBonuses = [],
}: Props) => {
  return useMemo(
    () =>
      computeBenchObjectsTotalScore({
        displayedTypedRules,
        benchObjects,
        selectedBenchObject,
        lineupBonuses,
      }),
    [selectedBenchObject, benchObjects, displayedTypedRules, lineupBonuses]
  );
};

useBenchObjectsTotalScore.fragments = {
  so5RuleInterface: gql`
    fragment useBenchObjectsTotalScore_so5RuleInterface on So5RuleInterface {
      id
      ... on So5AppearancesRule {
        id
        appearances(includeSubs: true) {
          id
          name
          sub
        }
      }
      ... on So5AverageScoresRule {
        id
        max
        immuneAppearances
      }
    }
  ` as TypedDocumentNode<useBenchObjectsTotalScore_so5RuleInterface>,
  composeTeamBenchObjectInterface: gql`
    fragment useBenchObjectsTotalScore_composeTeamBenchObjectInterface on ComposeTeamBenchObjectInterface {
      id
      lastTenPlayedSo5AverageScore: averageScore(
        type: LAST_TEN_PLAYED_SO5_AVERAGE_SCORE
      )
    }
  ` as TypedDocumentNode<useBenchObjectsTotalScore_composeTeamBenchObjectInterface>,
  so5AppearanceBonusInterface: gql`
    fragment useBenchObjectsTotalScore_so5AppearanceBonusInterface on So5AppearanceBonusInterface {
      ... on So5AverageScoresBonus {
        max
      }
    }
  ` as TypedDocumentNode<useBenchObjectsTotalScore_so5AppearanceBonusInterface>,
};
